import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { apiGetTradesHistory } from "api/apiTrades";
import PnlSummaryLine from "components/Strategies/PnlSummaryLine";
import { useTradingRefresh } from "components/Trading/tradingRefresh";
import StrategyLink from "components/Trading/StrategyLink";
import { downloadCsv } from "utils/csv";
import { DownloadOutlined } from "@ant-design/icons";
import GenericCard from "components/UI/GenericCard/GenericCard";
import AntTable from "components/UI/AntTable";
import { formatMomentDuration, returnUpDownClassname } from "utils/helpers";
import dayjs from "dayjs";
import { Button, DatePicker, Form, Popover } from "antd";
import { rangePresets } from "utils/constants";
import { QuestionCircleOutlined } from "@ant-design/icons";
import ProfitHistoryChart from "./ProfitHistoryChart";

const { RangePicker } = DatePicker;

const returnLast12Months = () => {
  const today = dayjs();
  const last12Months = [];
  for (let i = 0; i < 12; i++) {
    const month = today.subtract(i, "month");
    const startDate = month.startOf("month").format("YYYY-MM-DD");
    const endDate = month.endOf("month").format("YYYY-MM-DD");
    last12Months.push({
      month: month.format("MMMM YY"),
      startDate,
      endDate,
    });
  }
  return last12Months;
};

// the backend joins name/symbol; a trade whose strategy is gone keeps only its id
const strategyName = (trade) => trade.strategy?.name || "Deleted strategy";

const cents = (value) => (value == null ? "" : Math.round(value * 100) / 100);

const CSV_COLUMNS = [
  { title: "Symbol", value: (t) => t.symbol },
  { title: "Strategy", value: strategyName },
  { title: "Open date", value: (t) => t.openDate },
  { title: "Close date", value: (t) => t.closeDate },
  { title: "Contracts", value: (t) => t.amount },
  { title: "Buy price", value: (t) => t.openRate },
  { title: "Sell price", value: (t) => t.closeRate },
  { title: "Buy value", value: (t) => cents(t.openValue) },
  { title: "Sell value", value: (t) => cents(t.closeValue) },
  { title: "Fees", value: (t) => cents(t.premiumFee) },
  { title: "Net profit", value: (t) => cents(t.netProfit?.value) },
  { title: "Net profit %", value: (t) => t.netProfit?.perc },
  { title: "Signal", value: (t) => t.closeReason },
];

const TradesHistory = () => {
  const [tradesHistory, setTradesHistory] = useState([]);
  // the Strategy column filter (controlled, so the P&L line and the export
  // follow it, also after a reload)
  const [strategyFilter, setStrategyFilter] = useState(null);
  const shownTrades = useMemo(
    () =>
      strategyFilter?.length
        ? tradesHistory.filter((t) => strategyFilter.includes(strategyName(t)))
        : tradesHistory,
    [tradesHistory, strategyFilter],
  );
  const [form] = Form.useForm();
  // the Trading page can show two of these (Stocks and All)
  const formName = `search-trades-history-${useId().replace(/:/g, "")}`;
  const accountType = useSelector((state) => state.main.accountType);
  const { refreshKey } = useTradingRefresh();

  const last12MonthsArray = returnLast12Months();

  const getTradesHistory = async (values) => {
    let query = {
      accountType,
    };
    if (values) {
      const { dates } = values;
      if (dates) {
        const [startDate, endDate] = dates;
        query = {
          ...query,
          startDate: dayjs(startDate).format("YYYY-MM-DD"),
          endDate: dayjs(endDate).format("YYYY-MM-DD"),
        };
      }
    }

    const tradesHistory = await apiGetTradesHistory(query);
    setTradesHistory(tradesHistory);
  };

  const onSelectMonthHistory = (month) => {
    const { startDate, endDate } = month;
    form.setFieldValue("dates", [dayjs(startDate), dayjs(endDate)]);
    getTradesHistory({ dates: [dayjs(startDate), dayjs(endDate)] });
  };

  const sortByNumber = useCallback((a, b) => {
    // handle null / N/A values
    if (a === b) return 0;
    if (a === null || a === "N/A") return -1;
    if (b === null || b === "N/A") return 1;
    return a > b ? 1 : -1;
  }, []);

  const sortByText = useCallback((a, b) => {
    return a.toLowerCase().localeCompare(b.toLowerCase());
  }, []);

  const columns = [
    {
      title: "Details",
      dataIndex: "_id",
      render: (text, properties) => (
        <Popover
          content={
            <div>
              <div className="pb-2">
                <p className="font-semibold">Strategy Name:</p>
                <p>{strategyName(properties)}</p>
              </div>
              <div>
                <p className="font-semibold">Trade ID:</p>
                <p>{text}</p>
              </div>
            </div>
          }
          trigger="click"
        >
          <Button
            type="text"
            shape="circle"
            icon={<QuestionCircleOutlined />}
          />
        </Popover>
      ),
    },
    {
      title: "Symbol",
      dataIndex: "symbol",
      sorter: {
        compare: (a, b) => sortByText(a.symbol, b.symbol),
      },
    },
    {
      title: "Strategy",
      key: "strategy",
      render: (_, properties) => (
        <StrategyLink
          strategyId={properties.strategy?._id}
          name={properties.strategy?.name}
          instrument="stocks"
        />
      ),
      filters: [...new Set(tradesHistory.map(strategyName))]
        .sort()
        .map((name) => ({ text: name, value: name })),
      filteredValue: strategyFilter,
      onFilter: (value, properties) => strategyName(properties) === value,
      sorter: {
        compare: (a, b) => sortByText(strategyName(a), strategyName(b)),
      },
    },
    {
      title: "Buy Price",
      dataIndex: "openRate",
      render: (text, properties) => (
        <div>
          <div>{text}</div>
          <div>
            <span style={{ fontSize: 12 }}>
              ${properties.openValue.toLocaleString()}
            </span>
          </div>
        </div>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.openRate, b.openRate),
      },
    },
    {
      title: "Sell Price",
      dataIndex: "closeRate",
      render: (text, properties) => (
        <div>
          <div>{text}</div>
          <div>
            <span style={{ fontSize: 12 }}>
              ${properties.closeValue?.toLocaleString()}
            </span>
          </div>
        </div>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.closeRate, b.closeRate),
      },
    },
    {
      title: "Contracts",
      dataIndex: "amount",
      sorter: {
        compare: (a, b) => sortByNumber(a.amount, b.amount),
      },
    },
    {
      title: "Signal",
      dataIndex: "closeReason",
      sorter: {
        compare: (a, b) => sortByText(a.closeReason, b.closeReason),
      },
    },
    {
      title: "Net Profit",
      dataIndex: "profit.value",
      render: (text, properties) => (
        <span className={returnUpDownClassname(properties.netProfit.value)}>
          ${properties.netProfit.value.toLocaleString()}
        </span>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.netProfit.value, b.netProfit.value),
      },
    },
    {
      title: "Change",
      dataIndex: "profit.perc",
      render: (text, properties) => (
        <span className={returnUpDownClassname(properties.netProfit.perc)}>
          {properties.netProfit.perc}%
        </span>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.netProfit.perc, b.netProfit.perc),
      },
    },
    {
      title: "Run-up",
      dataIndex: "runup.perc",
      render: (text, properties) => (
        <div
          className={returnUpDownClassname(properties.runup.value)}
          title={`Rate: ${properties.runup.rate}`}
        >
          <div>${properties.runup.value.toLocaleString()}</div>
          <span style={{ fontSize: 12 }}>%{properties.runup.perc}</span>
        </div>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.runup.value, b.runup.value),
      },
    },
    {
      title: "Drawdown",
      dataIndex: "drawdown.perc",
      render: (text, properties) => (
        <div
          className={returnUpDownClassname(properties.drawdown.value)}
          title={`Rate: ${properties.drawdown.rate}`}
        >
          <div>${properties.drawdown.value.toLocaleString()}</div>
          <span style={{ fontSize: 12 }}>%{properties.drawdown.perc}</span>
        </div>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.drawdown.value, b.drawdown.value),
      },
    },
    {
      title: "Bars",
      dataIndex: "dealDaysOpen",
      sorter: {
        compare: (a, b) => sortByNumber(a.dealDaysOpen, b.dealDaysOpen),
      },
    },
    {
      title: "Duration",
      dataIndex: "openDate",
      render: (text, { closeDate }) => (
        <span>
          {formatMomentDuration(+new Date(closeDate) - +new Date(text))}
        </span>
      ),
      sorter: {
        compare: (a, b) => sortByText(a.openDate, b.openDate),
      },
    },
    {
      title: "Start",
      dataIndex: "openDate",
      render: (text) => <>{dayjs(text).format("HH:mm:ss - DD/MM/YY")}</>,
      sorter: {
        compare: (a, b) => sortByNumber(a.openDate, b.openDate),
      },
    },
    {
      title: "End",
      dataIndex: "closeDate",
      render: (text) => <>{dayjs(text).format("HH:mm:ss - DD/MM/YY")}</>,
      sorter: {
        compare: (a, b) => sortByNumber(a.closeDate, b.closeDate),
      },
    },
  ];

  // keeps the picked dates when another tab's change refreshes the history
  useEffect(() => {
    getTradesHistory(form.getFieldsValue());
  }, [accountType, refreshKey]);

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        name={formName}
        initialValues={{}}
        onFinish={getTradesHistory}
        className="max-w-[400px] pb-2"
      >
        <div className="flex items-end gap-4">
          <Form.Item name={"dates"} label="Dates">
            <RangePicker
              presets={rangePresets}
              format="DD/MM/YY"
              style={{
                width: "100%",
              }}
            />
          </Form.Item>
          <Form.Item shouldUpdate>
            {() => (
              <Button type="primary" htmlType="submit">
                Search
              </Button>
            )}
          </Form.Item>
        </div>
      </Form>
      <div className="flex gap-4 mb-4 flex-wrap">
        {last12MonthsArray.map((month) => (
          <Button
            key={month.month}
            onClick={() => onSelectMonthHistory(month)}
            // className="flex-1"
          >
            {month.month}
          </Button>
        ))}
      </div>
      <div className="cards-wrapper">
        <GenericCard
          title={`Trades History (${tradesHistory.length} trades)`}
          className="table-card"
          isCardOpenByDefault={true}
          maxHeight={610}
        >
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <PnlSummaryLine
                items={[
                  {
                    label: "Realized P&L",
                    tooltip:
                      "Total net P&L of the trades shown (dates and column filters)",
                    value: shownTrades.reduce(
                      (acc, item) => acc + (item.netProfit?.value || 0),
                      0,
                    ),
                  },
                ]}
              />
            </div>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              disabled={!shownTrades.length}
              onClick={() =>
                downloadCsv(
                  `trades-history-${dayjs().format("YYYY-MM-DD")}.csv`,
                  shownTrades,
                  CSV_COLUMNS,
                )
              }
            >
              Export CSV
            </Button>
          </div>
          <AntTable
            rowKey="_id"
            dataSource={tradesHistory}
            columns={columns}
            onChange={(pagination, filters) =>
              setStrategyFilter(filters.strategy || null)
            }
          />
        </GenericCard>
        <ProfitHistoryChart transactions={tradesHistory} />
      </div>
    </div>
  );
};

export default TradesHistory;
